import React, { Suspense, useEffect, useState } from 'react';
import { MAP_HEIGHT_DESKTOP, MAP_HEIGHT_MOBILE, MAP_REFRESH_MS } from 'constants/home';
import { useIsMobile } from 'hooks/useIsMobile';
import { useGetActivityHeatmapQuery, useGetActivityMapQuery, useGetHomeActivityStatsQuery } from 'store/services/activity/activityApi';
import HeatStrip from './HeatStrip';
import LivePulseTiles from './LivePulseTiles';
import './LiveMapModule.css';

// Map plus Live Pulse in ONE container (Asana 1218957945501621, Decision 1 and
// Decision 8). The map is a lazy chunk: world-atlas only ever loads here.

const CommunityTradeMap = React.lazy(() => import('components/CommunityTradeMap'));

/** Polling stops while the tab is hidden, so a hidden tab neither refetches nor animates. */
const useVisiblePolling = (interval: number): number => {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  return visible ? interval : 0;
};

export interface LiveMapModuleProps {
  /** Shared with the welcome header, which reads the same query */
  statsPolling?: boolean;
}

const LiveMapModule: React.FC<LiveMapModuleProps> = () => {
  const isMobile = useIsMobile();
  const pollingInterval = useVisiblePolling(MAP_REFRESH_MS);
  const { data: stats, isLoading: statsLoading } = useGetHomeActivityStatsQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true });
  const { data: map, isLoading: mapLoading } = useGetActivityMapQuery(undefined, { pollingInterval, skipPollingIfUnfocused: true });
  const { data: heat, isLoading: heatLoading } = useGetActivityHeatmapQuery(undefined, { refetchOnMountOrArgChange: true });
  const height = isMobile ? MAP_HEIGHT_MOBILE : MAP_HEIGHT_DESKTOP;

  return (
    <section className="home-live" data-testid="live-map-module" aria-label="Live trading activity">
      <Suspense fallback={<div className="home-live__map-fallback" style={{ height }} data-testid="live-map-fallback" />}>
        <CommunityTradeMap response={map} inTransitCount={stats?.inTransitCount ?? 0} lastTradeAt={stats?.mine?.lastTradeAt} isLoading={mapLoading} />
      </Suspense>
      <div className="home-pulse" data-testid="live-pulse">
        <LivePulseTiles stats={stats} isLoading={statsLoading} />
        <HeatStrip data={heat} isLoading={heatLoading} />
      </div>
    </section>
  );
};

export default LiveMapModule;
